import { Switch, Route, Redirect } from "wouter";
import { ClientLayout } from "@/components/layout/client-layout";
import ClientDashboard from "@/pages/portal/dashboard";
import ClientTickets from "@/pages/portal/tickets";
import ClientTicketDetail from "@/pages/portal/ticket-detail";
import ClientBilling from "@/pages/portal/billing";
import ClientTasks from "@/pages/portal/tasks";
import ClientInvoices from "@/pages/portal/invoices";
import ClientAccount from "@/pages/portal/account";
import ClientProjectsPage from "@/pages/portal/projects";
import ClientProjectDetailPage from "@/pages/portal/project-detail";
import ClientFilesPage from "@/pages/portal/files";
import ClientPartnersPage from "@/pages/portal/partners";
import NotFound from "@/pages/not-found";
import { useAuth } from "@/hooks/useAuth";
import { userHasPermission } from "@/lib/permissions";
import type { Permission } from "@shared/contracts/permissions";
import { useLayoutEffect, type ComponentType } from "react";
import { toast } from "@/hooks/use-toast";

const portalHomeCandidates: Array<{ permission: Permission; href: string }> = [
  { permission: "portal.dashboard.read", href: "/" },
  { permission: "portal.tickets.read", href: "/tickets" },
  { permission: "portal.projects.read", href: "/projects" },
  { permission: "portal.services.read", href: "/billing" },
  { permission: "portal.tasks.read", href: "/tasks" },
  { permission: "portal.invoices.read", href: "/invoices" },
  { permission: "portal.files.read", href: "/files" },
  { permission: "portal.partners.manage", href: "/partners" },
  { permission: "portal.account.read", href: "/account" },
];

function firstAllowedPortalPath(
  permissions: readonly Permission[] | undefined | null,
) {
  for (const candidate of portalHomeCandidates) {
    if (userHasPermission(permissions, candidate.permission)) {
      return candidate.href;
    }
  }
  return "/account";
}

function RequirePortalPermission({
  permission,
  component: Component,
}: {
  permission: Permission;
  component: ComponentType;
}) {
  const { user } = useAuth();
  const allowed = userHasPermission(user?.effectivePermissions, permission);
  const fallback = firstAllowedPortalPath(user?.effectivePermissions);

  useLayoutEffect(() => {
    if (!allowed) {
      toast({ title: "That page isn't available for this account." });
    }
  }, [allowed]);

  if (!allowed) {
    return <Redirect to={fallback} replace />;
  }
  return <Component />;
}

function PortalHome() {
  const { user } = useAuth();
  if (userHasPermission(user?.effectivePermissions, "portal.dashboard.read")) {
    return <ClientDashboard />;
  }
  return <Redirect to={firstAllowedPortalPath(user?.effectivePermissions)} />;
}

export function ClientRoutes() {
  return (
    <ClientLayout>
      <Switch>
        <Route path="/" component={PortalHome} />
        <Route path="/tickets/:id">
          {() => (
            <RequirePortalPermission
              permission="portal.tickets.read"
              component={ClientTicketDetail}
            />
          )}
        </Route>
        <Route path="/tickets">
          {() => (
            <RequirePortalPermission
              permission="portal.tickets.read"
              component={ClientTickets}
            />
          )}
        </Route>
        <Route path="/projects/:id">
          {() => (
            <RequirePortalPermission
              permission="portal.projects.read"
              component={ClientProjectDetailPage}
            />
          )}
        </Route>
        <Route path="/projects">
          {() => (
            <RequirePortalPermission
              permission="portal.projects.read"
              component={ClientProjectsPage}
            />
          )}
        </Route>
        <Route path="/billing">
          {() => (
            <RequirePortalPermission
              permission="portal.services.read"
              component={ClientBilling}
            />
          )}
        </Route>
        <Route path="/tasks">
          {() => (
            <RequirePortalPermission
              permission="portal.tasks.read"
              component={ClientTasks}
            />
          )}
        </Route>
        <Route path="/invoices">
          {() => (
            <RequirePortalPermission
              permission="portal.invoices.read"
              component={ClientInvoices}
            />
          )}
        </Route>
        <Route path="/files">
          {() => (
            <RequirePortalPermission
              permission="portal.files.read"
              component={ClientFilesPage}
            />
          )}
        </Route>
        <Route path="/partners">
          {() => (
            <RequirePortalPermission
              permission="portal.partners.manage"
              component={ClientPartnersPage}
            />
          )}
        </Route>
        <Route path="/account">
          {() => (
            <RequirePortalPermission
              permission="portal.account.read"
              component={ClientAccount}
            />
          )}
        </Route>
        <Route component={NotFound} />
      </Switch>
    </ClientLayout>
  );
}
